// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BackgroundImage, PinInput } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Anchor, Badge, Box, Button, Center, Container, Stack, Text, Title } from '@/components/ui';
import { IconArrowLeft, IconDeviceMobile, IconPhoto, IconCheck } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { AuthIllustration } from '../components';

export default function OtpIllustrated() {
  const [otp, setOtp] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleChange = (value: string) => {
    setOtp(value);
    setIsComplete(value.length === 6);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerified(true);
  };

  return (
    <Box style={{ minHeight: '100vh', display: 'flex' }}>
      {/* Left side - Illustration */}
      <Box
        style={{
          width: '50%',
          display: 'none',
          position: 'relative',
          overflow: 'hidden',
        }}
        visibleFrom="md"
      >
        <BackgroundImage
          src="https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200&q=80"
          style={{
            position: 'absolute',
            inset: 0,
          }}
        />
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${theme.colors.violet[9]}dd, ${theme.colors.purple[9]}cc)`,
          }}
        />
        <Center h="100%" p="xl" style={{ position: 'relative', zIndex: 1 }}>
          <Stack align="center" gap="xl">
            <SlideIn direction="down" delay={200}>
              <AuthIllustration type="otp" size={250} animated />
            </SlideIn>
            <FadeIn delay={400}>
              <Stack align="center" gap="sm">
                <Title order={2} c="white" ta="center">
                  Two-Factor Authentication
                </Title>
                <Text c="white" ta="center" opacity={0.9} maw={350}>
                  An extra layer of security for your account.
                </Text>
              </Stack>
            </FadeIn>
          </Stack>
        </Center>
      </Box>

      {/* Right side - Form */}
      <Box
        style={{
          flex: 1,
          backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.white,
        }}
      >
        <Center mih="100vh" p="xl">
          <Container size="xs" w="100%">
            {isVerified ? (
              <Stack gap="lg" align="center">
                <Center
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 20,
                    backgroundColor: 'var(--mantine-color-green-light)',
                  }}
                >
                  <IconCheck size={40} color="var(--mantine-color-green-6)" />
                </Center>

                <Stack align="center" gap="xs">
                  <Title order={2} ta="center">Verified!</Title>
                  <Text c="dimmed" ta="center" maw={300}>
                    Your identity has been successfully verified.
                  </Text>
                </Stack>

                <Button
                  component={Link}
                  to="/demo/auth-pages/login/illustrated"
                  fullWidth
                  size="md"
                  radius="md"
                  variant="gradient"
                  gradient={{ from: 'violet', to: 'purple' }}
                >
                  Continue
                </Button>
              </Stack>
            ) : (
              <Stack gap="lg">
                <Button
                  component={Link}
                  to="/demo/auth-pages/otp"
                  variant="subtle"
                  size="xs"
                  leftSection={<IconArrowLeft size={14} />}
                  w="fit-content"
                >
                  Back to variants
                </Button>

                <Stack align="center" gap="xs">
                  <Badge
                    leftSection={<IconPhoto size={12} />}
                    variant="gradient"
                    gradient={{ from: 'violet', to: 'purple' }}
                    size="lg"
                  >
                    Illustrated Design
                  </Badge>
                  <Center hiddenFrom="md" style={{ marginTop: 'var(--mantine-spacing-md)' }}>
                    <AuthIllustration type="otp" size={120} animated />
                  </Center>
                  <Center
                    visibleFrom="md"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      backgroundColor: 'var(--mantine-color-violet-light)',
                      marginTop: 'var(--mantine-spacing-md)',
                    }}
                  >
                    <IconDeviceMobile size={28} color="var(--mantine-color-violet-6)" />
                  </Center>
                  <Title order={2} ta="center" mt="sm">
                    Verification code
                  </Title>
                  <Text c="dimmed" size="sm" ta="center" maw={300}>
                    We've sent a 6-digit code to your phone ending in **42
                  </Text>
                </Stack>

                <form onSubmit={handleSubmit}>
                  <Stack gap="lg">
                    <Center>
                      <PinInput
                        length={6}
                        size="lg"
                        value={otp}
                        onChange={handleChange}
                        type="number"
                        oneTimeCode
                        aria-label="Verification code"
                      />
                    </Center>

                    <Button
                      type="submit"
                      fullWidth
                      size="md"
                      radius="md"
                      variant="gradient"
                      gradient={{ from: 'violet', to: 'purple' }}
                      disabled={!isComplete}
                    >
                      Verify code
                    </Button>
                  </Stack>
                </form>

                <Text ta="center" size="sm" c="dimmed">
                  Didn't receive the code?{' '}
                  <Anchor component="button" type="button" fw={500}>
                    Resend
                  </Anchor>
                </Text>

                <Center>
                  <Anchor component={Link} to="/demo/auth-pages/login/illustrated" size="sm">
                    <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Back to login
                  </Anchor>
                </Center>
              </Stack>
            )}
          </Container>
        </Center>
      </Box>
    </Box>
  );
}
